/**
 * Reaproveita os objetos da listagem anterior quando o conteúdo não mudou.
 *
 * O refresh de fundo do gerenciador (a cada 30 s, e a cada 2,5 s enquanto
 * chegam eventos de render) substitui o array inteiro de pastas e de artes.
 * Com objetos novos, o `memo(FolderCard)` e o `memo(AssetCard)` não poupam
 * nada: todos os cards são re-renderizados a cada ciclo — medido em
 * 16/10/2026, 40–107 ms com 3.000 pastas e 200–270 ms com 10.000, numa tela
 * parada. Devolver o **mesmo array** quando nada mudou, e o **mesmo objeto**
 * para cada item igual, é o que faz o memo valer de novo.
 *
 * A comparação é por valor, campo a campo (`jsonEqual`), porque o que chega
 * da API é JSON simples: um campo novo no contrato entra na comparação sem
 * ninguém lembrar de listá-lo aqui.
 */

/** Igualdade estrutural de dados JSON (objetos simples, arrays e primitivos). */
export function jsonEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== "object" || typeof b !== "object" || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let index = 0; index < a.length; index += 1) {
      if (!jsonEqual(a[index], b[index])) return false;
    }
    return true;
  }
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Object.keys(left);
  if (keys.length !== Object.keys(right).length) return false;
  for (const key of keys) {
    if (!Object.prototype.hasOwnProperty.call(right, key)) return false;
    if (!jsonEqual(left[key], right[key])) return false;
  }
  return true;
}

/**
 * `next` com os objetos de `previous` no lugar dos que não mudaram.
 *
 * - item com a mesma chave e o mesmo conteúdo → o objeto antigo;
 * - nada mudou (mesmos itens, mesma ordem) → o **próprio** array anterior,
 *   para quem depende dele (`useMemo`, selectors) nem acordar.
 */
export function reconcileList<T>(
  previous: readonly T[],
  next: readonly T[],
  keyOf: (item: T) => string,
  equal: (a: T, b: T) => boolean = jsonEqual,
): T[] {
  if (previous === next) return previous as T[];
  const byKey = new Map<string, T>();
  for (const item of previous) byKey.set(keyOf(item), item);

  let unchanged = previous.length === next.length;
  const merged = next.map((item, index) => {
    const old = byKey.get(keyOf(item));
    if (old !== undefined && equal(old, item)) {
      if (unchanged && previous[index] !== old) unchanged = false;
      return old;
    }
    unchanged = false;
    return item;
  });
  return unchanged ? (previous as T[]) : merged;
}
